import { useState, useMemo } from 'react'
import { Video, Zap, CheckCircle2, Link2, Users, Calendar, Phone, ArrowRight, Settings, Plus, X, Edit2, Trash2, Bell, Clock, MapPin, Check, BarChart3 } from 'lucide-react'
import { AddButton, ActionControls } from '../components/common/CRUDControls'
import { useMeetingsStore } from '../store/meetingsStore'
import { useNotifications } from '../hooks/useNotifications'
import NotificationSettings from '../components/meetings/NotificationSettings'

export default function MeetingsPage() {
  const meetings = useMeetingsStore((state) => state.meetings)
  const addMeeting = useMeetingsStore((state) => state.addMeeting)
  const updateMeeting = useMeetingsStore((state) => state.updateMeeting)
  const deleteMeeting = useMeetingsStore((state) => state.deleteMeeting)
  const getMeetingStats = useMeetingsStore((state) => state.getMeetingStats)
  const getTodaysMeetings = useMeetingsStore((state) => state.getTodaysMeetings)
  const getUpcomingReminders = useMeetingsStore((state) => state.getUpcomingReminders)

  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedMeeting, setSelectedMeeting] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showNotificationSettings, setShowNotificationSettings] = useState(false)

  // Initialize notifications
  const { notificationPermission, preferences, updatePreferences, testNotification } = useNotifications()

  const stats = useMemo(() => getMeetingStats(), [meetings])

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    organizer: '',
    organizerEmail: '',
    attendees: [],
    startTime: new Date(),
    endTime: new Date(),
    duration: 60,
    location: '',
    meetingType: 'video-conference',
    conferenceLink: '',
    recurrence: 'none',
  })

  const todaysMeetings = useMemo(() => getTodaysMeetings(), [meetings])
  const upcomingReminders = useMemo(() => getUpcomingReminders(24), [meetings])
  const filteredMeetings = useMemo(() => {
    return meetings.filter(m =>
      m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.organizer.toLowerCase().includes(searchQuery.toLowerCase())
    ).sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
  }, [meetings, searchQuery])

  const handleCreateMeeting = () => {
    setFormData({
      title: '',
      description: '',
      organizer: '',
      organizerEmail: '',
      attendees: [],
      startTime: new Date(),
      endTime: new Date(),
      duration: 60,
      location: '',
      meetingType: 'video-conference',
      conferenceLink: '',
      recurrence: 'none',
    })
    setShowCreateModal(true)
  }

  const handleEditMeeting = (meeting) => {
    setFormData(meeting)
    setSelectedMeeting(meeting.id)
    setShowEditModal(true)
  }

  const handleSaveMeeting = () => {
    if (formData.title && formData.organizer) {
      if (showEditModal && selectedMeeting) {
        updateMeeting(selectedMeeting, formData)
      } else {
        addMeeting(formData)
      }
      setShowCreateModal(false)
      setShowEditModal(false)
      setSelectedMeeting(null)
    }
  }

  const handleDeleteMeeting = (id) => {
    if (window.confirm('Delete this meeting?')) {
      deleteMeeting(id)
      setSelectedMeeting(null)
    }
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  }

  const platforms = [
    {
      name: 'Google Meet',
      icon: '🎥',
      status: 'Connected',
      color: 'from-blue-600 to-blue-500',
      participants: '156 meetings',
      features: ['Screen sharing', 'Recording', 'Live captions', 'Up to 500 participants'],
      action: 'Launch Google Meet',
      status_color: 'bg-green-500/20 text-green-300'
    },
    {
      name: 'Microsoft Teams',
      icon: '💼',
      status: 'Connected',
      color: 'from-purple-600 to-purple-500',
      participants: '189 meetings',
      features: ['Team chat', 'Persistent chat', 'Channel meetings', 'Up to 10,000 participants'],
      action: 'Open Microsoft Teams',
      status_color: 'bg-green-500/20 text-green-300'
    },
    {
      name: 'Zoom',
      icon: '📱',
      status: 'Connected',
      color: 'from-cyan-600 to-cyan-500',
      participants: '234 meetings',
      features: ['HD video', 'Screen share', 'Virtual backgrounds', 'Breakout rooms'],
      action: 'Start Zoom Meeting',
      status_color: 'bg-green-500/20 text-green-300'
    },
    {
      name: 'Proton Meet',
      icon: '🔐',
      status: 'Connected',
      color: 'from-indigo-600 to-indigo-500',
      participants: '89 meetings',
      features: ['End-to-end encrypted', 'No account required', 'Screen sharing', 'Up to 300 participants'],
      action: 'Create Proton Meet',
      status_color: 'bg-green-500/20 text-green-300'
    },
    {
      name: 'Cybertection Center Meet',
      icon: '⭐',
      status: 'Active',
      color: 'from-amber-600 to-amber-500',
      participants: '179 meetings',
      features: ['Native integration', 'Real-time analytics', 'AI transcription', 'Meeting recordings'],
      action: 'Start Cybertection Meet',
      status_color: 'bg-amber-500/20 text-amber-300'
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-600 p-8 text-white shadow-2xl border border-blue-500/20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-blue-400/10 rounded-full -mr-48 -mt-48 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-400/10 rounded-full -ml-40 -mb-40 blur-3xl"></div>

          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Video className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">Meetings & Conferencing</h1>
                </div>
                <p className="text-blue-100 text-lg font-semibold">Unified Video Conferencing Platform - All Tools Integrated</p>
                <p className="text-blue-200 text-sm mt-1">Google Meet • Microsoft Teams • Zoom • Proton Meet • Cybertection Center Meet</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.activeConferences}</div>
                <div className="text-sm text-blue-100">Active Now</div>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: 'Total Meetings', value: stats.total, icon: Calendar, color: 'from-blue-600 to-blue-500', textColor: 'text-blue-400' },
            { label: 'Upcoming', value: stats.upcoming, icon: Clock, color: 'from-cyan-600 to-cyan-500', textColor: 'text-cyan-400' },
            { label: 'Video Conferences', value: stats.videoConference, icon: Video, color: 'from-green-600 to-green-500', textColor: 'text-green-400' },
            { label: 'In-Person', value: stats.inPerson, icon: Users, color: 'from-purple-600 to-purple-500', textColor: 'text-purple-400' },
            { label: 'Avg Attendees', value: stats.avgAttendees, icon: Users, color: 'from-orange-600 to-orange-500', textColor: 'text-orange-400' },
          ].map((stat, idx) => {
            const Icon = stat.icon
            return (
              <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
                    <p className={`${stat.textColor} text-4xl font-black mt-3`}>{stat.value}</p>
                  </div>
                  <div className={`bg-gradient-to-br ${stat.color} p-4 rounded-xl shadow-lg shadow-blue-500/20`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Meeting Platforms Integration */}
        <div>
          <h2 className="text-3xl font-bold text-white mb-6 flex items-center space-x-3">
            <Video className="w-7 h-7 text-blue-400" />
            <span>Integrated Meeting Platforms</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {platforms.map((platform, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg hover:shadow-blue-500/20"
              >
                <div className={`h-1 w-full bg-gradient-to-r ${platform.color} rounded-full mb-4 shadow-lg`}></div>

                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className={`bg-gradient-to-br ${platform.color} p-3 rounded-lg shadow-lg text-xl`}>
                      {platform.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-white text-xl">{platform.name}</h3>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full inline-block mt-1 ${platform.status_color}`}>
                        {platform.status}
                      </span>
                    </div>
                  </div>
                  <Link2 className="w-5 h-5 text-blue-400 opacity-50 group-hover:opacity-100 transition-opacity" />
                </div>

                <p className="text-cyan-400 text-sm font-semibold mb-4">{platform.participants}</p>

                <div className="mb-4">
                  <p className="text-blue-300 text-xs font-semibold mb-2">Features:</p>
                  <div className="flex flex-wrap gap-2">
                    {platform.features.map((feature, fidx) => (
                      <span key={fidx} className="text-xs bg-slate-700/50 text-blue-300 px-2 py-1 rounded border border-blue-500/20">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <button className={`w-full py-2.5 px-3 bg-gradient-to-r ${platform.color} hover:shadow-lg rounded-lg text-white text-sm font-bold flex items-center justify-between transition-all group-hover:shadow-lg group-hover:shadow-blue-500/30`}>
                  <span>{platform.action}</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Meeting Controls & Settings */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="lg:col-span-2 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <Zap className="w-5 h-5 text-blue-400" />
              <span>Quick Actions</span>
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Schedule Meeting', icon: '📅', action: 'Schedule' },
                { label: 'Start Instant Meeting', icon: '▶️', action: 'Start' },
                { label: 'Join Meeting', icon: '🔗', action: 'Join' },
                { label: 'View Recordings', icon: '📹', action: 'View' },
                { label: 'Meeting Transcripts', icon: '📝', action: 'Browse' },
                { label: 'Test Notification', icon: '🔔', action: 'Test', onClick: () => {
                  if (meetings.length > 0) {
                    testNotification(meetings[0])
                  }
                } },
              ].map((action, idx) => (
                <button
                  key={idx}
                  onClick={action.onClick}
                  className="bg-slate-700/50 hover:bg-slate-700 rounded-lg p-4 border border-blue-500/20 hover:border-blue-400/40 transition-all group"
                >
                  <div className="text-2xl mb-2">{action.icon}</div>
                  <p className="text-white text-sm font-semibold">{action.label}</p>
                  <p className="text-blue-400 text-xs mt-1">{action.action}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Settings & Preferences */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <Settings className="w-5 h-5 text-blue-400" />
              <span>Settings</span>
            </h3>
            <div className="space-y-3 mb-4">
              {[
                { setting: 'Video Quality', value: '1080p' },
                { setting: 'Audio Device', value: 'System Default' },
                { setting: 'Background', value: 'Blur' },
                { setting: 'Recording Storage', value: 'Cloud' },
                { setting: 'Auto-recording', value: 'Enabled' },
              ].map((item, idx) => (
                <div key={idx} className="bg-slate-700/50 rounded-lg p-3 border border-blue-500/20">
                  <div className="flex items-center justify-between">
                    <p className="text-white text-sm font-semibold">{item.setting}</p>
                    <span className="text-cyan-400 text-xs font-bold">{item.value}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Notification Settings Button */}
            <button
              onClick={() => setShowNotificationSettings(true)}
              className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 shadow-lg"
            >
              <Bell className="w-4 h-4" />
              Notification Settings
            </button>

            <p className="text-xs text-slate-400 mt-3">
              {notificationPermission ? '✓ Desktop notifications enabled' : '○ Desktop notifications disabled'}
            </p>
          </div>
        </div>

        {/* Notifications & Search */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Upcoming Reminders */}
          <div className="lg:col-span-1 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <Bell className="w-5 h-5 text-orange-400" />
              <span>Next 24 Hours</span>
            </h3>
            <div className="space-y-3">
              {upcomingReminders.length === 0 ? (
                <p className="text-slate-400 text-sm">No meetings in the next 24 hours</p>
              ) : (
                upcomingReminders.map((meeting) => (
                  <div key={meeting.id} className="bg-slate-700/50 rounded-lg p-3 border border-orange-500/20">
                    <p className="text-white text-sm font-semibold truncate">{meeting.title}</p>
                    <p className="text-orange-400 text-xs mt-1">{formatTime(meeting.startTime)}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Today's Schedule */}
          <div className="lg:col-span-1 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <Calendar className="w-5 h-5 text-cyan-400" />
              <span>Today's Schedule</span>
            </h3>
            <div className="space-y-3">
              {todaysMeetings.length === 0 ? (
                <p className="text-slate-400 text-sm">No meetings scheduled for today</p>
              ) : (
                todaysMeetings.map((meeting) => (
                  <div key={meeting.id} className="bg-slate-700/50 rounded-lg p-3 border border-cyan-500/20 cursor-pointer hover:border-cyan-400/40 transition-all"
                    onClick={() => setSelectedMeeting(meeting.id)}>
                    <p className="text-white text-sm font-semibold truncate">{meeting.title}</p>
                    <p className="text-cyan-400 text-xs mt-1">{formatTime(meeting.startTime)} ({meeting.duration}m)</p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Meeting Statistics */}
          <div className="lg:col-span-1 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
            <h3 className="font-bold text-white mb-4 flex items-center space-x-2 text-lg">
              <BarChart3 className="w-5 h-5 text-purple-400" />
              <span>Statistics</span>
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-300">Completed:</span>
                <span className="text-white font-semibold">{stats.completed}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-300">This Month:</span>
                <span className="text-white font-semibold">{Math.round(stats.total / 4)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-300">Video Calls:</span>
                <span className="text-blue-400 font-semibold">{Math.round((stats.videoConference / stats.total) * 100)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Meetings List */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 shadow-lg">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-white flex items-center space-x-2 text-lg">
              <Calendar className="w-5 h-5 text-blue-400" />
              <span>All Meetings</span>
            </h3>
            <button
              onClick={handleCreateMeeting}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Schedule Meeting
            </button>
          </div>

          {/* Search and Filter */}
          <input
            type="text"
            placeholder="Search meetings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 bg-slate-700 border border-blue-500/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-blue-400 mb-4"
          />

          {/* Meetings Cards */}
          <div className="grid grid-cols-1 gap-4">
            {filteredMeetings.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-400">No meetings found</p>
              </div>
            ) : (
              filteredMeetings.map((meeting) => (
                <div
                  key={meeting.id}
                  onClick={() => setSelectedMeeting(meeting.id)}
                  className={`bg-slate-700/50 rounded-lg p-4 border transition-all cursor-pointer ${
                    selectedMeeting === meeting.id
                      ? 'border-blue-400 bg-slate-700'
                      : 'border-blue-500/20 hover:border-blue-400/40'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <p className="text-white font-semibold text-lg">{meeting.title}</p>
                      <p className="text-slate-400 text-sm">{meeting.description}</p>
                    </div>
                    {meeting.meetingType === 'video-conference' && <Video className="w-5 h-5 text-blue-400" />}
                    {meeting.meetingType === 'in-person' && <MapPin className="w-5 h-5 text-green-400" />}
                    {meeting.meetingType === 'conference-call' && <Phone className="w-5 h-5 text-purple-400" />}
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-3 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <Clock className="w-4 h-4 text-slate-500" />
                      <div>
                        <div className="text-xs text-slate-500">Date & Time</div>
                        <div>{formatDate(meeting.startTime)} {formatTime(meeting.startTime)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <Users className="w-4 h-4 text-slate-500" />
                      <div>
                        <div className="text-xs text-slate-500">Attendees</div>
                        <div>{meeting.attendees.length} people</div>
                      </div>
                    </div>
                  </div>

                  {meeting.location && (
                    <div className="flex items-center gap-2 text-sm text-slate-300 mb-3">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      <span>{meeting.location}</span>
                    </div>
                  )}

                  {meeting.conferenceLink && (
                    <div className="mb-3">
                      <a href={meeting.conferenceLink} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-2">
                        <Link2 className="w-4 h-4" />
                        Join Conference
                      </a>
                    </div>
                  )}

                  {selectedMeeting === meeting.id && (
                    <div className="border-t border-slate-600 pt-4 mt-4 flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEditMeeting(meeting)
                        }}
                        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteMeeting(meeting.id)
                        }}
                        className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

      </div>

      {/* Notification Settings Modal */}
      <NotificationSettings
        isOpen={showNotificationSettings}
        onClose={() => setShowNotificationSettings(false)}
        preferences={preferences}
        onUpdatePreferences={updatePreferences}
      />

      {/* Create/Edit Meeting Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-700 sticky top-0 bg-slate-800">
              <h2 className="text-xl font-semibold text-white">
                {showEditModal ? 'Edit Meeting' : 'Schedule New Meeting'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  setShowEditModal(false)
                  setSelectedMeeting(null)
                }}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Meeting Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                  placeholder="e.g., Weekly Team Standup"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 resize-none"
                  rows="3"
                  placeholder="Meeting details and agenda..."
                />
              </div>

              {/* Organizer Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Organizer Name *</label>
                  <input
                    type="text"
                    value={formData.organizer}
                    onChange={(e) => setFormData({ ...formData, organizer: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.organizerEmail}
                    onChange={(e) => setFormData({ ...formData, organizerEmail: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={formData.startTime.toISOString().split('T')[0]}
                    onChange={(e) => {
                      const newDate = new Date(e.target.value)
                      setFormData({ ...formData, startTime: newDate })
                    }}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Start Time</label>
                  <input
                    type="time"
                    value={formData.startTime.toISOString().slice(11, 16)}
                    onChange={(e) => {
                      const [hours, minutes] = e.target.value.split(':')
                      const newDate = new Date(formData.startTime)
                      newDate.setHours(parseInt(hours), parseInt(minutes))
                      setFormData({ ...formData, startTime: newDate })
                    }}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Duration (minutes)</label>
                  <input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => {
                      const duration = parseInt(e.target.value)
                      const endTime = new Date(formData.startTime.getTime() + duration * 60000)
                      setFormData({ ...formData, duration, endTime })
                    }}
                    min="15"
                    step="15"
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Meeting Type and Location */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Meeting Type</label>
                  <select
                    value={formData.meetingType}
                    onChange={(e) => setFormData({ ...formData, meetingType: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="video-conference">Video Conference</option>
                    <option value="in-person">In-Person</option>
                    <option value="conference-call">Conference Call</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Location</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                    placeholder="e.g., Conference Room A"
                  />
                </div>
              </div>

              {/* Conference Link */}
              {formData.meetingType !== 'in-person' && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Conference Link</label>
                  <input
                    type="url"
                    value={formData.conferenceLink}
                    onChange={(e) => setFormData({ ...formData, conferenceLink: e.target.value })}
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-400 focus:outline-none focus:border-blue-500"
                    placeholder="https://meet.google.com/..."
                  />
                </div>
              )}

              {/* Recurrence */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Recurrence</label>
                <select
                  value={formData.recurrence}
                  onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="none">No Recurrence</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 border-t border-slate-700 pt-6 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false)
                    setShowEditModal(false)
                    setSelectedMeeting(null)
                  }}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveMeeting}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  {showEditModal ? 'Update' : 'Schedule'} Meeting
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
